import React from 'react'
import { useTranslation } from 'react-i18next'
import { copyButtonIconColour } from 'styles/theme/colours'
import { Box } from '@node-space/storybook-components/dist/Box'
import { PagingSidePanelType } from '@node-space/storybook-components/dist/components/SidePanel/types'
import CopyButton from '@node-space/storybook-components/dist/CopyButton'
import { SidePanel, SidePanelHeader } from '@node-space/storybook-components/dist/SidePanel'
import { Text } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { ContactCase } from 'types/contacts'
import { PaymentContextType } from 'types/payments'
import { highlightRowBasedOnId } from 'utils/table/table'
import { useContactCaseSidePanelHelper } from '../hooks/useContactCaseSidePanelHelper'
import { ContactCaseSidePanelBody } from './ContactCaseSidePanelBody'

export type ContactCaseSummaryProps = {
  isOpen: boolean
  contactCase: ContactCase
  pageBeneficiaries: PagingSidePanelType
  onClose: () => void
}

export const ContactCaseSummary = ({
  isOpen,
  contactCase,
  pageBeneficiaries,
  onClose,
}: ContactCaseSummaryProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const paymentContextType = contactCase?.paymentContext?.type

  const { isLoading, payment, channel, isPaymentIn, isChannelPayment, isDirectDeposit, isError } =
    useContactCaseSidePanelHelper({
      contactCase,
      paymentContextType,
    })

  const getSecondaryLabel = () => {
    switch (paymentContextType) {
      case PaymentContextType.CHANNEL_PAYMENT:
        return t('contacts.channelPayment')
      case PaymentContextType.PAYMENT_IN:
        return t('contacts.paymentIn')
      case PaymentContextType.DEPOSIT:
        return t('contacts.userDeposit')
      default:
        return
    }
  }

  const primaryLabel = `${contactCase?.amount} ${contactCase?.currencyCode}`
  const showReference = !isLoading && !isError && !isDirectDeposit

  const iconUrl = contactCase?.currencyCode
    ? `${process.env.ICONS_URL}${contactCase.currencyCode?.toLowerCase()}.svg`
    : process.env.ICONS_URL

  return (
    <SidePanel
      isOpen={isOpen}
      paging={pageBeneficiaries}
      onClose={onClose}
      activeRowId={contactCase?.hash}
      onActiveRowChange={highlightRowBasedOnId}
      externalStyle={{ top: `${bannerSpacing}px` }}
      sidePanelHeader={{
        customSidePanelHeader: (
          <SidePanelHeader
            heading={{
              iconUrls: [iconUrl],
              primaryLabel: <Text variant="heading-3">{primaryLabel}</Text>,
              secondaryLabel: (
                <Box className="md:flex w-full">
                  <Text variant="body-text" color="subdued">
                    {getSecondaryLabel()}
                  </Text>
                </Box>
              ),
            }}
            {...(showReference && {
              bottomContent: {
                leftLabel: t('reference'),
                leftDescription: contactCase?.externalId && (
                  <Box flex width="full" alignItems="center">
                    <Text
                      tag="span"
                      variant="body-text"
                      color="subdued"
                      className="mb-1 text-ellipsis overflow-hidden text-nowrap"
                    >
                      {isPaymentIn && payment?.reference}
                      {isChannelPayment && channel?.reference}
                    </Text>
                    <CopyButton
                      copyText={contactCase?.externalId}
                      confirmationMessage={t('copiedToClipboard')}
                      iconColour={copyButtonIconColour}
                      confirmationMessageAlignment="right"
                      confirmationMessageTheme="dark"
                    />
                  </Box>
                ),
              },
            })}
            template={2}
          />
        ),
      }}
    >
      <Box padding={24} tag="section" width="full" background="white">
        <ContactCaseSidePanelBody contactCase={contactCase} />
      </Box>
    </SidePanel>
  )
}
